import * as React from 'react';
import { Box, Typography, Grid, Menu, MenuItem } from '@mui/material';

import Container from '../../../components/Layout/Container';
import { Heading2, Heading3 } from '../../../components/Heading';
import LocalImage from '../../../components/LocalImage';
import { formatCompactNumber } from '../../../utils';

import KeyboardArrowDownIcon from '@mui/icons-material/KeyboardArrowDown';
import CalendarTodayIcon from '@mui/icons-material/CalendarToday';
import StarIcon from '../../../assets/img/week/star.webp';
import { useCallback } from 'react';

const periods = [
  "Compare To: All-Time Average",
  "Compare To: Last Week"
];

const ExerciseDetails = ({
  data
}) => {
  const [anchorEl, setAnchorEl] = React.useState({});
  const [selectedPeriod, setSelectedPeriod] = React.useState({
    details: periods[0]
  });

  const open = {
    details: Boolean(anchorEl.details),
  }
  const handleOpenMenu = (event, key) => {
    setAnchorEl({
      ...anchorEl,
      [key]: event.currentTarget,
    });
  };

  const handleClose = (key) => {
    setAnchorEl({
      ...anchorEl,
      [key]: null,
    });
  };

  const handleSelect = (newPeriod, key) => {
    setSelectedPeriod({
      ...selectedPeriod,
      [key]: newPeriod,
    });
    setAnchorEl({
      ...anchorEl,
      [key]: null,
    });
  };

  const getExerciseDetailsFromKey = useCallback((key) => {
    try {
      return data?.directDamageDetails.find(item => item.exerciseKey === key);
    } catch (e) {
      return {};
    }
  }, [data]);

  return (
    <Container sx={{ marginTop: 8 }}>
      <Box
        display="flex"
        flexDirection={{
          xs: "column",
          sm: "row"
        }}
        gap={{
          xs: 4,
          sm: 8
        }}
      >
        <Heading2 sx={{ '& br': { display: { xs: 'none', md: 'block' } } }}>Exercise Details</Heading2>
        <Box display="flex" mt={0.5}>
          <Box
            sx={{
              borderRadius: "8px",
              backgroundColor: "rgb(238, 238, 238)",
              color: "rgb(33, 43, 54)",
              height: "36px",
              position: "relative",
              paddingLeft: "10px",
              paddingRight: "30px",
            }}
            mr={1}
            onClick={(event) => handleOpenMenu(event, "details")}
          >
            <Typography
              sx={{ cursor: "pointer" }}
              fontSize={14}
              lineHeight="36px"
              display="flex"
              alignItems="center"
            >
              {selectedPeriod.details}
              <CalendarTodayIcon sx={{ marginLeft: 1, fontSize: 14 }} />
            </Typography>
            <Box
              sx={{
                position: "absolute",
                right: "0px",
                top: "6px",
              }}
            >
              <KeyboardArrowDownIcon />
            </Box>
          </Box>
          <Menu
            anchorEl={anchorEl.details}
            open={open.details}
            onClose={() => handleClose("details")}
            anchorOrigin={{
              vertical: 'bottom',
              horizontal: 'left',
            }}
            PaperProps={{
              style: {},
            }}
          >
            {periods.map((option) => (
              <MenuItem key={option} selected={option === selectedPeriod.details} onClick={() => handleSelect(option, "details")}>
                {option}
              </MenuItem>
            ))}
          </Menu>
        </Box>
      </Box>
      <Box sx={{
        display: "flex",
        alignItems: "center",
        marginTop: 4,
      }}>
        <img
          src={StarIcon}
          alt="Star"
          width="24px"
          height="24px"
        />
        <Typography sx={{
          fontSize: "14px",
          lineHeight: "24px",
          fontWeight: 700,
          marginLeft: 1,
        }}>
          = New Personal Record!
        </Typography>
      </Box>
      <Grid
        container
        display={{
          xs: 'none',
          md: 'flex'
        }}
        mt={3}
      >
        <Grid item md={4} mb={3}>
          <Typography sx={{
            fontSize: "14px",
            lineHeight: "22px",
            opacity: 0.8,
          }}>
            Exercise
          </Typography>
        </Grid>
        <Grid item md={2}>
          <Typography sx={{
            fontSize: "14px",
            lineHeight: "22px",
            opacity: 0.8,
            textAlign: "center",
          }}>
            Sets
          </Typography>
        </Grid>
        <Grid item md={2}>
          <Typography sx={{
            fontSize: "14px",
            lineHeight: "22px",
            opacity: 0.8,
            textAlign: "center",
          }}>
            Reps
          </Typography>
        </Grid>
        <Grid item md={2}>
          <Typography sx={{
            fontSize: "14px",
            lineHeight: "22px",
            opacity: 0.8,
            textAlign: "center",
          }}>
            Work (Ft.-Lbs.)
          </Typography>
        </Grid>
        <Grid item md={2}>
          <Typography sx={{
            fontSize: "14px",
            lineHeight: "22px",
            opacity: 0.8,
            textAlign: "center",
          }}>
            Total Volume (Lbs.)
          </Typography>
        </Grid>
      </Grid>
      <Grid
        container
        mt={{
          xs: 2,
          sm: 0
        }}
        rowSpacing={2}
      >
        {Object.keys(data?.currentWeek?.weeklyRecap?.exerciseStats || {}).map(item => <React.Fragment key={item}>
          <Grid item xs={12} md={4} rowSpacing={2}>
            <Box sx={{
              display: "flex",
            }}>
              <img
                src={getExerciseDetailsFromKey(item)?.maleStartOfRepImage}
                width="132"
                height="132"
                alt="no-cage"
              />
              <Box ml={1}>
                <Heading3>{getExerciseDetailsFromKey(item)?.exerciseName}</Heading3>
                <Typography sx={{
                  fontSize: "16px",
                  lineHeight: "22px",
                  marginTop: 1,
                }}>
                  {getExerciseDetailsFromKey(item)?.attackName || getExerciseDetailsFromKey(item)?.name}
                </Typography>
                <Typography sx={{
                  fontSize: "16px",
                  lineHeight: "22px",
                  color: "#CECECE",
                  marginTop: 1,
                }}>
                  {getExerciseDetailsFromKey(item)?.musclesWorked}
                </Typography>
              </Box>
            </Box>
          </Grid>
          <Grid item xs={3} md={2} sx={{
            textAlign: "center"
          }}>
            <Box
              display="flex"
              flexDirection="column"
              height="100%"
              alignItems="center"
            >
              <Typography
                display={{
                  md: 'none'
                }}
                flexGrow={1}
                mb={1}
                sx={{
                  fontSize: "14px",
                  lineHeight: "22px",
                  opacity: 0.8,
                }}
              >
                Sets
              </Typography>
              {
                data?.currentWeek?.weeklyRecap?.exerciseStats[item].is_sets_personal_best && <img
                  src={StarIcon}
                  alt="Star"
                  width="24px"
                  height="24px"
                />
              }
              <Heading2>{formatCompactNumber(data?.currentWeek?.weeklyRecap?.exerciseStats[item]?.total_sets)}</Heading2>
              <Box sx={{
                display: "flex",
                justifyContent: "center",
                marginTop: "3px",
              }}>
                <Typography sx={{
                  fontSize: "16px",
                  lineHeight: "22px",
                  opacity: 0.8,
                }}>
                  {formatCompactNumber(data?.previousWeek?.weeklyRecap?.exerciseStats[item]?.total_sets)}
                </Typography>
                {
                  ((data?.currentWeek?.weeklyRecap?.exerciseStats[item]?.total_sets || 0) <
                    (data?.previousWeek?.weeklyRecap?.exerciseStats[item]?.total_sets || 0)) &&
                  <LocalImage src="img/week/arrow-narrow-down.webp" height="22" alt="no-cage" loading="lazy" />
                }
                {
                  ((data?.currentWeek?.weeklyRecap?.exerciseStats[item]?.total_sets || 0) >
                    (data?.previousWeek?.weeklyRecap?.exerciseStats[item]?.total_sets || 0)) &&
                  <LocalImage src="img/week/arrow-narrow-up.webp" height="22" alt="no-cage" loading="lazy" />
                }
              </Box>
            </Box>
          </Grid>
          <Grid item xs={3} md={2} sx={{
            textAlign: "center"
          }}>
            <Box
              display="flex"
              flexDirection="column"
              height="100%"
              alignItems="center"
            >
              <Typography
                display={{
                  md: 'none'
                }}
                flexGrow={1}
                mb={1}
                sx={{
                  fontSize: "14px",
                  lineHeight: "22px",
                  opacity: 0.8,
                }}
              >
                Reps
              </Typography>
              {
                data?.currentWeek?.weeklyRecap?.exerciseStats[item].is_reps_personal_best && <img
                  src={StarIcon}
                  alt="Star"
                  width="24px"
                  height="24px"
                />
              }
              <Heading2>{formatCompactNumber(data?.currentWeek?.weeklyRecap?.exerciseStats[item]?.total_reps)}</Heading2>
              <Box sx={{
                display: "flex",
                justifyContent: "center",
                marginTop: "3px",
              }}>
                <Typography sx={{
                  fontSize: "16px",
                  lineHeight: "22px",
                  opacity: 0.8,
                }}>
                  {formatCompactNumber(data?.previousWeek?.weeklyRecap?.exerciseStats[item]?.total_reps)}
                </Typography>
                {
                  ((data?.currentWeek?.weeklyRecap?.exerciseStats[item]?.total_reps || 0) <
                    (data?.previousWeek?.weeklyRecap?.exerciseStats[item]?.total_reps || 0)) &&
                  <LocalImage src="img/week/arrow-narrow-down.webp" height="22" alt="no-cage" loading="lazy" />
                }
                {
                  ((data?.currentWeek?.weeklyRecap?.exerciseStats[item]?.total_reps || 0) >
                    (data?.previousWeek?.weeklyRecap?.exerciseStats[item]?.total_reps || 0)) &&
                  <LocalImage src="img/week/arrow-narrow-up.webp" height="22" alt="no-cage" loading="lazy" />
                }
              </Box>
            </Box>
          </Grid>
          <Grid item xs={3} md={2} sx={{
            textAlign: "center"
          }}>
            <Box
              display="flex"
              flexDirection="column"
              height="100%"
              alignItems="center"
            >
              <Typography
                display={{
                  md: 'none'
                }}
                flexGrow={1}
                mb={1}
                sx={{
                  fontSize: "14px",
                  lineHeight: "22px",
                  opacity: 0.8,
                }}
              >
                Work (Ft.-Lbs.)
              </Typography>
              {
                data?.currentWeek?.weeklyRecap?.exerciseStats[item].is_work_personal_best && <img
                  src={StarIcon}
                  alt="Star"
                  width="24px"
                  height="24px"
                />
              }
              <Heading2>{formatCompactNumber(data?.currentWeek?.weeklyRecap?.exerciseStats[item]?.total_work)}</Heading2>
              <Box sx={{
                display: "flex",
                justifyContent: "center",
                marginTop: "3px",
              }}>
                <Typography sx={{
                  fontSize: "16px",
                  lineHeight: "22px",
                  opacity: 0.8,
                }}>
                  {formatCompactNumber(data?.previousWeek?.weeklyRecap?.exerciseStats[item]?.total_work)}
                </Typography>
                {
                  ((data?.currentWeek?.weeklyRecap?.exerciseStats[item]?.total_work || 0) <
                    (data?.previousWeek?.weeklyRecap?.exerciseStats[item]?.total_work || 0)) &&
                  <LocalImage src="img/week/arrow-narrow-down.webp" height="22" alt="no-cage" loading="lazy" />
                }
                {
                  ((data?.currentWeek?.weeklyRecap?.exerciseStats[item]?.total_work || 0) >
                    (data?.previousWeek?.weeklyRecap?.exerciseStats[item]?.total_work || 0)) &&
                  <LocalImage src="img/week/arrow-narrow-up.webp" height="22" alt="no-cage" loading="lazy" />
                }
              </Box>
            </Box>
          </Grid>
          <Grid item xs={3} md={2} sx={{
            textAlign: "center"
          }}>
            <Box
              display="flex"
              flexDirection="column"
              height="100%"
              alignItems="center"
            >
              <Typography
                display={{
                  md: 'none'
                }}
                flexGrow={1}
                mb={1}
                sx={{
                  fontSize: "14px",
                  lineHeight: "22px",
                  opacity: 0.8,
                }}
              >
                Total Volumn (Lbs.)
              </Typography>
              {
                data?.currentWeek?.weeklyRecap?.exerciseStats[item].is_volume_personal_best && <img
                  src={StarIcon}
                  alt="Star"
                  width="24px"
                  height="24px"
                />
              }
              <Heading2>{formatCompactNumber(data?.currentWeek?.weeklyRecap?.exerciseStats[item]?.total_volume)}</Heading2>
              <Box sx={{
                display: "flex",
                justifyContent: "center",
                marginTop: "3px",
              }}>
                <Typography sx={{
                  fontSize: "16px",
                  lineHeight: "22px",
                  opacity: 0.8,
                }}>
                  {formatCompactNumber(data?.previousWeek?.weeklyRecap?.exerciseStats[item]?.total_volume)}
                </Typography>
                {
                  ((data?.currentWeek?.weeklyRecap?.exerciseStats[item]?.total_volume || 0) <
                    (data?.previousWeek?.weeklyRecap?.exerciseStats[item]?.total_volume || 0)) &&
                  <LocalImage src="img/week/arrow-narrow-down.webp" height="22" alt="no-cage" loading="lazy" />
                }
                {
                  ((data?.currentWeek?.weeklyRecap?.exerciseStats[item]?.total_volume || 0) >
                    (data?.previousWeek?.weeklyRecap?.exerciseStats[item]?.total_volume || 0)) &&
                  <LocalImage src="img/week/arrow-narrow-up.webp" height="22" alt="no-cage" loading="lazy" />
                }
              </Box>
            </Box>
          </Grid>
        </React.Fragment>)}
      </Grid>
    </Container>
  );
};

export default ExerciseDetails;
